import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: number, title: string, price: number, available: boolean, image: string | null }} VariantJson
 * @typedef {{ id: number, title: string, handle: string, productType: string, collectionHandle: string, featured_image: string | null, variants: VariantJson[] }} ProductJson
 * @typedef {{
 *   moneyFormat: string,
 *   currency: string,
 *   maxCharms: number,
 *   emptyMessage: string,
 *   labels: Record<string, string>,
 *   bases: ProductJson[],
 *   charms: ProductJson[],
 *   baseTypeFilter?: TypeFilterConfig,
 *   charmTypeFilter?: TypeFilterConfig,
 * }} BuilderConfig
 */

/** @typedef {{ enabled: boolean, default?: string, options: { value: string, label: string }[], handlesByOption: Record<string, string[]> }} TypeFilterConfig */

class BraceletBuilderComponent extends HTMLElement {
  /** @type {BuilderConfig | null} */
  #config = null;

  /** @type {{ panels: HTMLElement, errorEl: HTMLElement, summaryBlock: HTMLElement, summaryList: HTMLElement, totalEl: HTMLElement, submitBtn: HTMLButtonElement }} */
  #refs = /** @type {any} */ ({});

  /** @type {number | null} */
  #baseProductIndex = null;

  /** @type {number | null} */
  #baseVariantId = null;

  /** @type {Set<number>} */
  #charmVariantIds = new Set();

  /** @type {string} */
  #baseType = '';

  /** @type {string} */
  #charmType = '';

  connectedCallback() {
    const script = this.querySelector('script[type="application/json"]');
    this.#refs = {
      panels: /** @type {HTMLElement} */ (this.querySelector('[data-ref="panels"]')),
      errorEl: /** @type {HTMLElement} */ (this.querySelector('[data-ref="errorEl"]')),
      summaryBlock: /** @type {HTMLElement} */ (this.querySelector('[data-ref="summaryBlock"]')),
      summaryList: /** @type {HTMLElement} */ (this.querySelector('[data-ref="summaryList"]')),
      totalEl: /** @type {HTMLElement} */ (this.querySelector('[data-ref="totalEl"]')),
      submitBtn: /** @type {HTMLButtonElement} */ (this.querySelector('[data-ref="submitBtn"]')),
    };

    if (!(script instanceof HTMLScriptElement) || !script.textContent?.trim()) {
      this.#bootstrapFail('Falta la configuración del configurador.');
      return;
    }

    try {
      this.#config = JSON.parse(script.textContent);
    } catch (e) {
      console.error('bracelet-builder: JSON inválido', e);
      this.#bootstrapFail('No se pudo cargar el catálogo. Recarga la página.');
      return;
    }

    if (!this.#refs.panels || !this.#refs.submitBtn) return;

    this.#render();
    this.#syncUiState();
    this.#refs.panels.addEventListener('click', this.#onPanelsClick);
    this.#refs.panels.addEventListener('change', this.#onPanelsChange);
    this.#refs.panels.addEventListener('keydown', this.#onPanelsKeydown);
    this.#refs.submitBtn.addEventListener('click', this.#onSubmit);
  }

  /** @param {string} msg */
  #bootstrapFail(msg) {
    const { panels, submitBtn } = this.#refs;
    if (panels) {
      panels.hidden = false;
      panels.innerHTML = `<p class="bracelet-builder__empty">${this.#escapeHtml(msg)}</p>`;
    }
    if (submitBtn instanceof HTMLButtonElement) submitBtn.disabled = true;
  }

  disconnectedCallback() {
    this.#refs.panels?.removeEventListener('click', this.#onPanelsClick);
    this.#refs.panels?.removeEventListener('change', this.#onPanelsChange);
    this.#refs.panels?.removeEventListener('keydown', this.#onPanelsKeydown);
    this.#refs.submitBtn?.removeEventListener('click', this.#onSubmit);
  }

  #money(cents) {
    const c = this.#config;
    if (!c) return '';
    return formatMoney(cents, c.moneyFormat, c.currency);
  }

  #firstAvailableVariant(product) {
    const v = product.variants.find((x) => x.available);
    return v ?? product.variants[0] ?? null;
  }

  #productFullyUnavailable(product) {
    return !product.variants.some((v) => v.available);
  }

  #render() {
    const cfg = this.#config;
    const { panels } = this.#refs;
    if (!cfg || !panels) return;

    const basesAll = cfg.bases ?? [];
    const charmsAll = cfg.charms ?? [];

    const baseTypeFilter = cfg.baseTypeFilter;
    const charmTypeFilter = cfg.charmTypeFilter;

    if (baseTypeFilter?.default && this.#baseType === '') {
      this.#baseType = baseTypeFilter.default;
    }
    if (charmTypeFilter?.enabled && charmTypeFilter.default && this.#charmType === '') {
      this.#charmType = charmTypeFilter.default;
    }

    const baseType = this.#baseType;
    const charmType = this.#charmType;
    const basesFiltered = this.#filterByCollectionHandle(basesAll, baseTypeFilter, baseType);
    const charmsFiltered = this.#filterByCollectionHandle(charmsAll, charmTypeFilter, charmType);

    if (basesAll.length === 0 && charmsAll.length === 0) {
      panels.hidden = false;
      panels.innerHTML = `<p class="bracelet-builder__empty">${this.#escapeHtml(cfg.emptyMessage)}</p>`;
      this.#refs.submitBtn.disabled = true;
      return;
    }

    panels.hidden = false;
    const lb = cfg.labels;

    const baseTitle = lb.chooseBase;

    const baseFilterHtml = this.#renderTypeFilterHtml(baseTypeFilter, baseType, 'base');
    const charmFilterHtml = this.#renderTypeFilterHtml(charmTypeFilter, charmType, 'charm');

    panels.innerHTML = `
      <div class="bracelet-builder__panel" data-panel="bases">
        <h3 class="bracelet-builder__panel-title">${this.#escapeHtml(baseTitle)}</h3>
        ${baseFilterHtml}
        ${basesFiltered.length === 0 ? `<p class="bracelet-builder__empty">${this.#escapeHtml(cfg.emptyMessage)}</p>` : `<ul class="bracelet-builder__grid">${basesFiltered.map((entry) => this.#renderProductCard(entry.product, entry.originalIndex, 'base')).join('')}</ul>`}
      </div>
      <div class="bracelet-builder__panel" data-panel="charms">
        <h3 class="bracelet-builder__panel-title">${this.#escapeHtml(lb.chooseCharms)}</h3>
        ${charmFilterHtml}
        ${charmsFiltered.length === 0 ? `<p class="bracelet-builder__empty">${this.#escapeHtml(cfg.emptyMessage)}</p>` : `<ul class="bracelet-builder__grid">${charmsFiltered.map((entry) => this.#renderProductCard(entry.product, entry.originalIndex, 'charm')).join('')}</ul>`}
      </div>
    `;

    this.#syncUiState();
  }

  /**
   * @param {ProductJson} product
   * @param {number} index
   * @param {'base' | 'charm'} kind
   */
  #renderProductCard(product, index, kind) {
    const cfg = this.#config;
    const disabled = this.#productFullyUnavailable(product);
    const img =
      product.featured_image ||
      product.variants.find((v) => v.image)?.image ||
      '';
    const showSelect = product.variants.length > 1;
    const variantLabel = cfg?.labels.variantLabel ?? '';

    const placeholderHtml = `<div class="bracelet-builder__variant-select-placeholder" aria-hidden="true"></div>`;
    const selectHtml = showSelect
      ? `<select class="bracelet-builder__variant-select" data-kind="${kind}" data-product-index="${index}" aria-label="${this.#escapeHtml(variantLabel)}">
          ${product.variants
            .map((v) => {
              return `<option value="${v.id}" ${!v.available ? 'disabled' : ''} ${v.id === this.#firstAvailableVariant(product)?.id ? 'selected' : ''}>
            ${this.#escapeHtml(v.title === 'Default Title' ? product.title : `${product.title} — ${v.title}`)}
          </option>`;
            })
            .join('')}
        </select>`
      : placeholderHtml;

    const cardClass =
      kind === 'base'
        ? `bracelet-builder__card bracelet-builder__card--base${disabled ? ' is-disabled' : ''}`
        : `bracelet-builder__card bracelet-builder__card--charm${disabled ? ' is-disabled' : ''}`;

    const singleVariantId = !showSelect && product.variants[0] ? product.variants[0].id : '';

    return `
      <li>
        <div
          class="${cardClass}"
          data-kind="${kind}"
          data-product-index="${index}"
          data-variant-id="${singleVariantId}"
          tabindex="${disabled ? '-1' : '0'}"
          role="button"
          aria-pressed="false"
          aria-disabled="${disabled}"
        >
          <div class="bracelet-builder__card-media">
            ${img ? `<img src="${this.#escapeHtml(img)}" alt="" loading="lazy" width="300" height="300">` : ''}
          </div>
          <div class="bracelet-builder__card-body">
            <p class="bracelet-builder__card-title">${this.#escapeHtml(product.title)}</p>
            ${selectHtml}
            ${!showSelect && product.variants[0] && !product.variants[0].available ? `<p class="bracelet-builder__card-title" style="margin-top:0.35rem;font-style:italic;">${this.#escapeHtml(cfg?.labels.soldOut ?? '')}</p>` : ''}
          </div>
        </div>
      </li>
    `;
  }

  /** Returns array of { product, originalIndex } */
  #filterByCollectionHandle(productsAll, typeFilter, activeType) {
    const all = productsAll.map((p, i) => ({ product: p, originalIndex: i }));
    if (!typeFilter?.enabled) return all;
    if (activeType === 'all') return all;
    const handleMap = typeFilter.handlesByOption ?? {};
    const handles = handleMap[activeType];
    if (!Array.isArray(handles) || handles.length === 0) return all;
    return all.filter((entry) => handles.includes(entry.product.collectionHandle));
  }

  /**
   * @param {TypeFilterConfig | undefined} typeFilter
   * @param {string} activeType
   * @param {'base' | 'charm'} scope
   */
  #renderTypeFilterHtml(typeFilter, activeType, scope) {
    if (!typeFilter?.enabled || !typeFilter.options?.length) return '';
    return `<div class="bracelet-builder__base-type-filter" style="--filter-count:${typeFilter.options.length}" data-ref="${scope}TypeFilter">
            ${typeFilter.options
              .map(
                (opt) =>
                  `<button type="button" class="bracelet-builder__filter-btn${opt.value === activeType ? ' is-active' : ''}" data-filter-scope="${scope}" data-filter-value="${opt.value}">${this.#escapeHtml(opt.label)}</button>`
              )
              .join('')}
        </div>`;
  }

  /** @param {string} s */
  #escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  #syncUiState() {
    const cfg = this.#config;
    if (!cfg || !this.#refs.panels) return;

    this.#refs.panels.querySelectorAll('.bracelet-builder__card--base').forEach((el) => {
      const i = Number(/** @type {HTMLElement} */ (el).dataset.productIndex);
      const pressed = this.#baseProductIndex === i;
      el.classList.toggle('is-selected', pressed);
      el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });

    const selectedBase = this.#refs.panels.querySelector('.bracelet-builder__card--base.is-selected');
    if (selectedBase instanceof HTMLElement) {
      const select = selectedBase.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        this.#baseVariantId = Number(select.value);
      } else if (selectedBase.dataset.variantId) {
        this.#baseVariantId = Number(selectedBase.dataset.variantId);
      }
    } else {
      this.#baseVariantId = null;
    }

    this.#refs.panels.querySelectorAll('.bracelet-builder__card--charm').forEach((el) => {
      const vid = this.#charmVariantIdFromCard(/** @type {HTMLElement} */ (el));
      const pressed = vid != null && this.#charmVariantIds.has(vid);
      el.classList.toggle('is-selected', pressed);
      el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });

    this.#updateSummary();

    const baseOk = this.#baseVariantId != null && this.#variantAvailable(this.#baseVariantId, cfg.bases);
    this.#refs.submitBtn.disabled = !baseOk;
  }

  /**
   * @param {HTMLElement} card
   */
  #charmVariantIdFromCard(card) {
    const select = card.querySelector('select');
    if (select instanceof HTMLSelectElement) return Number(select.value);
    const id = card.dataset.variantId;
    return id ? Number(id) : null;
  }

  /**
   * @param {number} variantId
   * @param {ProductJson[]} products
   */
  #variantAvailable(variantId, products) {
    for (const p of products) {
      const v = p.variants.find((x) => x.id === variantId);
      if (v) return v.available;
    }
    return false;
  }

  #updateSummary() {
    const cfg = this.#config;
    const { summaryBlock, summaryList, totalEl } = this.#refs;
    if (!cfg || !summaryBlock || !summaryList || !totalEl) return;

    const items = [];
    let totalCents = 0;

    if (this.#baseVariantId != null) {
      const line = this.#lineForVariant(this.#baseVariantId, cfg.bases);
      if (line) {
        items.push(line.label);
        totalCents += line.price;
      }
    }

    for (const vid of this.#charmVariantIds) {
      const line = this.#lineForVariant(vid, cfg.charms);
      if (line) {
        items.push(line.label);
        totalCents += line.price;
      }
    }

    if (items.length === 0) {
      summaryBlock.hidden = true;
      totalEl.textContent = '—';
      return;
    }

    summaryBlock.hidden = false;
    summaryList.innerHTML = items.map((t) => `<li>${this.#escapeHtml(t)}</li>`).join('');
    totalEl.textContent = this.#money(totalCents);
  }

  /**
   * @param {number} variantId
   * @param {ProductJson[]} products
   */
  #lineForVariant(variantId, products) {
    for (const p of products) {
      const v = p.variants.find((x) => x.id === variantId);
      if (v) {
        const title = v.title === 'Default Title' || v.title === 'Default title' ? p.title : `${p.title} — ${v.title}`;
        return { label: title, price: v.price };
      }
    }
    return null;
  }

  /** @param {KeyboardEvent} e */
  #onPanelsKeydown = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;
    if (t.classList.contains('bracelet-builder__card') && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      t.click();
    }
  };

  /** @param {Event} e */
  #onPanelsChange = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLSelectElement)) return;
    this.#clearError();

    if (t.dataset.ref === 'baseTypeSelect') {
      this.#baseType = /** @type {string} */ (t.value);
      this.#baseProductIndex = null;
      this.#baseVariantId = null;
      this.#render();
      return;
    }


    const kind = t.dataset.kind;
    const pi = Number(t.dataset.productIndex);
    const cfg = this.#config;
    if (kind === 'base' && this.#baseProductIndex === pi) {
      this.#baseVariantId = Number(t.value);
      this.#syncUiState();
    }
    if (kind === 'charm' && cfg?.charms[pi]) {
      const card = t.closest('.bracelet-builder__card--charm');
      const wasSelected = card instanceof HTMLElement && card.classList.contains('is-selected');
      if (!wasSelected) return;

      const product = cfg.charms[pi];
      const newVid = Number(t.value);
      for (const vid of [...this.#charmVariantIds]) {
        if (product.variants.some((v) => v.id === vid)) {
          this.#charmVariantIds.delete(vid);
        }
      }
      if (product.variants.find((v) => v.id === newVid)?.available) {
        this.#charmVariantIds.add(newVid);
      }
      this.#syncUiState();
    }
  };

  /** @param {Event} e */
  #onPanelsClick = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    this.#clearError();
    const filterBtn = t.closest('.bracelet-builder__filter-btn');
    if (filterBtn instanceof HTMLElement && filterBtn.dataset.filterValue) {
      const scope = filterBtn.dataset.filterScope ?? 'base';
      if (scope === 'charm') {
        this.#charmType = filterBtn.dataset.filterValue;
        this.#render();
      } else {
        this.#baseType = filterBtn.dataset.filterValue;
        this.#baseProductIndex = null;
        this.#baseVariantId = null;
        this.#render();
      }
      return;
    }

    const card = t.closest('.bracelet-builder__card');
    if (!(card instanceof HTMLElement) || card.classList.contains('is-disabled')) return;
    if (t.closest('select')) return;

    const kind = card.dataset.kind;
    const pi = Number(card.dataset.productIndex);
    const cfg = this.#config;
    if (!cfg) return;

    if (kind === 'base') {
      this.#baseProductIndex = pi;
      const select = card.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        this.#baseVariantId = Number(select.value);
      } else {
        const vid = card.dataset.variantId;
        this.#baseVariantId = vid ? Number(vid) : null;
      }
      this.#syncUiState();
      return;
    }

    if (kind === 'charm') {
      const vid = this.#charmVariantIdFromCard(card);
      if (vid == null || !this.#variantAvailable(vid, cfg.charms)) return;

      if (this.#charmVariantIds.has(vid)) {
        this.#charmVariantIds.delete(vid);
        card.classList.remove('is-selected');
        card.setAttribute('aria-pressed', 'false');
      } else {
        if (this.#charmVariantIds.size >= cfg.maxCharms) {
          this.#showError(cfg.labels.maxCharmsError ?? '');
          return;
        }
        this.#charmVariantIds.add(vid);
      }
      this.#syncUiState();
    }
  };

  #clearError() {
    const { errorEl } = this.#refs;
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  /** @param {string} msg */
  #showError(msg) {
    const { errorEl } = this.#refs;
    if (errorEl && msg) {
      errorEl.hidden = false;
      errorEl.textContent = msg;
    }
  }

  #onSubmit = async () => {
    const cfg = this.#config;
    if (!cfg || !this.#baseVariantId) return;

    this.#clearError();
    const kitId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `kit-${Date.now()}`;
    const summaryText = this.#buildPersonalizationSummary(kitId);

    /** @type {{ id: number, quantity: number, properties: Record<string, string> }[]} */
    const items = [
      {
        id: this.#baseVariantId,
        quantity: 1,
        properties: {
          _kit_id: kitId,
          Personalización: summaryText,
        },
      },
    ];

    for (const vid of this.#charmVariantIds) {
      items.push({
        id: vid,
        quantity: 1,
        properties: {
          _kit_id: kitId,
          Personalización: summaryText,
        },
      });
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [...cartItemsComponents]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const payload = {
      items,
      sections: sectionIds.join(','),
    };

    const sourceId = this.dataset.sectionId || 'bracelet-builder';
    const btn = this.#refs.submitBtn;
    btn.disabled = true;

    try {
      const response = await fetch(/** @type {any} */ (window).Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        const message = data.message || '';
        const description = data.description || '';
        this.dispatchEvent(new CartErrorEvent(sourceId, message, description, data.errors ?? {}));
        this.#showError(message || description || 'Error al añadir al carrito.');
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      this.dispatchEvent(
        new CartAddEvent(cart, sourceId, {
          source: 'bracelet-builder-component',
          itemCount: items.reduce((s, x) => s + x.quantity, 0),
          sections: data.sections,
        })
      );

      this.#charmVariantIds.clear();
      this.#syncUiState();
    } catch (err) {
      console.error(err);
      this.#showError('No se pudo conectar. Inténtalo de nuevo.');
    } finally {
      this.#syncUiState();
    }
  };

  /**
   * @param {string} kitId
   */
  #buildPersonalizationSummary(kitId) {
    const cfg = this.#config;
    if (!cfg || !this.#baseVariantId) return '';

    const parts = [`Ref: ${kitId}`];
    const bLine = this.#lineForVariant(this.#baseVariantId, cfg.bases);
    if (bLine) parts.push(`Pulsera: ${bLine.label}`);

    const charmLabels = [...this.#charmVariantIds]
      .map((vid) => this.#lineForVariant(vid, cfg.charms)?.label)
      .filter(Boolean);
    parts.push(charmLabels.length ? `Charms: ${charmLabels.join(', ')}` : 'Charms: —');

    return parts.join(' — ');
  }
}

if (!customElements.get('bracelet-builder-component')) {
  customElements.define('bracelet-builder-component', BraceletBuilderComponent);
}
